import { useCallback, useEffect, useState } from 'react';
import { Plus, Percent } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { toast } from '../../lib/toast';
import CommissionOverrideModal from './CommissionOverrideModal';

type Scope = 'account' | 'supervisor';

interface OverrideRow {
  _id: string;
  recipientId: string;
  kind: 'permanent' | 'temporary';
  rate: number;
  startDate: string;
  endDate?: string;
  status: 'active' | 'ended' | 'cancelled';
  note?: string;
}

const statusStyles: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-800',
  ended: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-700'
};

const BLURB: Record<Scope, string> = {
  account: "Each override redirects a slice of this account's AM commission to another recipient.",
  supervisor:
    'Each override pays a recipient a percentage of the floor-catalog price on every order under ' +
    'this supervisor. It comes out of the house margin, not any AM\'s commission.'
};

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Commission-override list + admin add/end/cancel controls for ONE target —
 * a clinic (scope 'account') or a supervisor (scope 'supervisor'). Rendered inside
 * the corresponding detail view.
 */
export default function CommissionOverridesPanel({
  scope = 'account',
  targetId,
  targetName,
  canManage
}: {
  scope?: Scope;
  targetId: string;
  targetName?: string;
  canManage: boolean;
}) {
  const [rows, setRows] = useState<OverrideRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = scope === 'supervisor'
      ? await apiClient.getSupervisorCommissionOverrides(targetId)
      : await apiClient.getAccountCommissionOverrides(targetId);
    setLoading(false);
    if (res.success) {
      setRows((res as any).overrides || res.data?.overrides || []);
    }
  }, [scope, targetId]);

  useEffect(() => { load(); }, [load]);

  const handleEnd = async (id: string) => {
    setBusyId(id);
    const res = await apiClient.endCommissionOverride(id);
    setBusyId(null);
    if (res.success) { toast.success('Override ended'); load(); }
    else toast.error(res.error || 'Failed to end override');
  };

  const handleCancel = async (id: string) => {
    setBusyId(id);
    const res = await apiClient.cancelCommissionOverride(id);
    setBusyId(null);
    if (res.success) { toast.success('Override cancelled'); load(); }
    else toast.error(res.error || 'Failed to cancel override');
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Percent size={16} className="text-gray-500" />
          <h4 className="font-semibold text-gray-900">Commission Overrides</h4>
        </div>
        {canManage && (
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
          >
            <Plus size={14} /> Add
          </button>
        )}
      </div>

      <p className="text-xs text-gray-400 mb-3">{BLURB[scope]}</p>

      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-400">
          No overrides on this {scope === 'supervisor' ? 'supervisor' : 'account'}.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map(row => (
            <div key={row._id} className="flex items-center justify-between border border-gray-100 rounded-md px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{row.rate}%</span>
                  <span className="text-xs text-gray-500 capitalize">{row.kind}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${statusStyles[row.status]}`}>{row.status}</span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {fmtDate(row.startDate)}{row.kind === 'temporary' ? ` → ${fmtDate(row.endDate)}` : ''}
                  {row.note ? ` · ${row.note}` : ''}
                </div>
              </div>
              {canManage && row.status === 'active' && (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    disabled={busyId === row._id}
                    onClick={() => handleEnd(row._id)}
                    className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
                  >End</button>
                  <button
                    disabled={busyId === row._id}
                    onClick={() => handleCancel(row._id)}
                    className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                  >Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <CommissionOverrideModal
          scope={scope}
          targetId={targetId}
          targetName={targetName}
          onClose={() => setShowModal(false)}
          onCreated={load}
        />
      )}
    </div>
  );
}
